import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import PasswordInput from '../components/PasswordInput';
import { authService } from '../services/api';
import InvoiceHistory from '../components/InvoiceHistory';
import CancelSubscription from '../components/CancelSubscription';
import DeleteAccount from '../components/DeleteAccount';
import BillingAddressForm from '../components/BillingAddressForm';

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { user, updateUser, disable2FA } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [is2FALoading, setIs2FALoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
      }));
    }
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDisable2FA = async () => {
    setIs2FALoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const result = await disable2FA();
      if (result.success) {
        setSuccess('Authentification à deux facteurs désactivée avec succès.');
      } else {
        setError(result.error || 'Erreur lors de la désactivation de la 2FA');
      }
    } catch (err: any) {
      setError('Erreur lors de la désactivation de la 2FA');
      console.error('Erreur désactivation 2FA:', err);
    } finally {
      setIs2FALoading(false);
    }
  };

  // Fonction pour mettre à jour les informations de profil (nom, prénom)
  const handleProfileUpdate = async () => {
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      // Préparer les données pour la mise à jour du profil (uniquement nom et prénom)
      const profileData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
      };
      
      // Appel API pour mettre à jour le profil
      const updatedUser = await authService.updateProfile(profileData);

      // Mettre à jour le contexte utilisateur
      if (updateUser) {
        updateUser(updatedUser);
      }

      setSuccess('Profil mis à jour avec succès');
      setIsEditing(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la mise à jour du profil');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fonction pour changer le mot de passe
  const handlePasswordChange = async () => {
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      // Vérifier que le mot de passe actuel est fourni
      if (!formData.currentPassword) {
        setError('Veuillez entrer votre mot de passe actuel');
        setIsSubmitting(false);
        return;
      }
      
      // Vérifier que le nouveau mot de passe est fourni
      if (!formData.newPassword) {
        setError('Veuillez entrer un nouveau mot de passe');
        setIsSubmitting(false);
        return;
      }
      
      // Vérifier que les mots de passe correspondent
      if (formData.newPassword !== formData.confirmPassword) {
        setError('Les mots de passe ne correspondent pas');
        setIsSubmitting(false);
        return;
      }

      // Appel API pour changer le mot de passe
      const result = await authService.changePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });

      if (result.success) {
        setSuccess(result.message || 'Mot de passe changé avec succès');
        
        // Réinitialiser les champs de mot de passe
        setFormData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        }));
      } else {
        setError(result.error || 'Erreur lors du changement de mot de passe');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors du changement de mot de passe');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Gestion du formulaire principal
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Si un changement de mot de passe est demandé
    if (formData.newPassword) {
      await handlePasswordChange();
    } else {
      // Sinon, mise à jour du profil uniquement
      await handleProfileUpdate();
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handlePasswordChange();
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Mon profil</h1>
      
      <div className="grid grid-cols-1 gap-6">
        {/* Informations de profil */}
        <Card title="Informations personnelles">
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}
            
            {success && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
                {success}
              </div>
            )}
            
            <div className="mb-4">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-gray-100"
              />
              <p className="text-xs text-gray-500 mt-1">L'email ne peut pas être modifié</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                  Prénom
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${!isEditing ? 'bg-gray-100' : ''}`}
                />
              </div>
              
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                  Nom
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${!isEditing ? 'bg-gray-100' : ''}`}
                />
              </div>
            </div>
            
            <div className="flex justify-end">
              {!isEditing ? (
                <Button type="button" onClick={() => setIsEditing(true)}>
                  Modifier
                </Button>
              ) : (
                <div className="flex space-x-3">
                  <Button 
                    type="button" 
                    variant="secondary" 
                    onClick={() => {
                      setIsEditing(false);
                      if (user) {
                        setFormData(prev => ({
                          ...prev,
                          firstName: user.firstName || '',
                          lastName: user.lastName || '',
                        }));
                      }
                    }}
                    disabled={isSubmitting}
                  >
                    Annuler
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                </div>
              )}
            </div>
          </form>
        </Card>
        
        {/* Changement de mot de passe */}
        <Card title="Changer de mot de passe">
          <form onSubmit={handlePasswordSubmit}>
            <div className="mb-4">
              <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Mot de passe actuel
              </label>
              <PasswordInput
                value={formData.currentPassword}
                onChange={handleChange}
                label=""
                placeholder="Entrez votre mot de passe actuel"
                required={true}
                className="w-full"
              />
            </div>
            
            <div className="mb-4">
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Nouveau mot de passe
              </label>
              <PasswordInput
                value={formData.newPassword}
                onChange={handleChange}
                label=""
                placeholder="Entrez votre nouveau mot de passe"
                required={true}
                className="w-full"
              />
            </div>
            
            <div className="mb-4">
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Confirmer le nouveau mot de passe
              </label>
              <PasswordInput
                value={formData.confirmPassword}
                onChange={handleChange}
                label=""
                placeholder="Confirmez votre nouveau mot de passe"
                required={true}
                className="w-full"
              />
            </div>
            
            <div className="flex justify-end">
              <Button 
                type="submit" 
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Modification...' : 'Changer le mot de passe'}
              </Button>
            </div>
          </form>
        </Card>
        
        {/* Authentification à deux facteurs */}
        <Card title="Authentification à deux facteurs">
          <div className="mb-4">
            <p className="text-gray-600">
              {user?.twoFA 
                ? 'L\'authentification à deux facteurs est activée sur votre compte.' 
                : 'L\'authentification à deux facteurs n\'est pas activée sur votre compte.'}
            </p>
            
            {success && <div className="mb-4 p-2 bg-green-100 text-green-800 rounded">{success}</div>}
            {error && <div className="mb-4 p-2 bg-red-100 text-red-800 rounded">{error}</div>}
            
            <Button
              type="button"
              onClick={() => user?.twoFA ? handleDisable2FA() : navigate('/2fa-setup')}
              variant={user?.twoFA ? 'secondary' : 'primary'}
              disabled={is2FALoading}
            >
              {is2FALoading ? 'Chargement...' : (user?.twoFA ? 'Désactiver' : 'Activer')}
            </Button>
          </div>
          
          <div>
            <h3 className="text-lg font-medium mb-2">Abonnement</h3>
            <p className="text-gray-600 mb-4">
              Plan actuel: <span className="font-semibold">{user?.plan?.name || 'Gratuit'}</span>
            </p>
            <p className="text-gray-600 mb-2">
              <span className="inline-block w-32">Agents:</span> <span className="font-semibold">{user?.plan?.maxClients || 1}</span>
            </p>
            <p className="text-gray-600 mb-2">
              <span className="inline-block w-32">Requêtes/mois:</span> <span className="font-semibold">{user?.plan?.requestsPerMonth?.toLocaleString() || '1 000'}</span>
            </p>
            <p className="text-gray-600 mb-4">
              <span className="inline-block w-32">Bande passante:</span> <span className="font-semibold">{user?.plan?.bandwidth || 1} Go</span>
            </p>
            
            <Button
              type="button"
              onClick={() => navigate('/subscription')}
            >
              Gérer mon abonnement
            </Button>
          </div>
        </Card>
        
        {/* Adresse de facturation */}
        <Card title="Adresse de facturation">
          <div className="mb-4">
            <p className="text-gray-600 mb-4">
              Ces informations seront utilisées pour vos factures et pour le calcul de la TVA.
            </p>
            <BillingAddressForm />
          </div>
        </Card>
        
        {/* Historique des factures */}
        <InvoiceHistory />
        
        {/* Gestion de l'abonnement */}
        <CancelSubscription />
        
        {/* Suppression du compte */}
        <DeleteAccount />
      </div>
    </div>
  );
};

export default Profile;
